import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'

import { sortBy as _sortBy } from 'lodash/fp'

import { Observable, shareReplay } from 'rxjs'
import { map, tap } from 'rxjs/operators'

import { APPLICATIONS, FlxEnumeration, StaticAsset } from '@flowx/core-sdk'
import { NomenclatorValue } from '@flowx/core-sdk'

import { StaticAssetsStore } from '../store/static-assets.store'
import { SubstitutionTagsStore } from '../store/substitution-tags.store'

type StaticAssetsCache = Record<string, Observable<{ key: string; storagePath: string }[]>>
type EnumerationsCache = Record<string, Observable<NomenclatorValue[]>>

@Injectable({ providedIn: 'root' })
export class EnumerationService {
  readonly #http = inject(HttpClient)
  readonly #substitutionTagsStore = inject(SubstitutionTagsStore)
  readonly #staticAssetsStore = inject(StaticAssetsStore)

  staticAssetsCache: StaticAssetsCache = {}
  enumerationsCache: EnumerationsCache = {}

  getEnumerations(
    baseUrl: string,
    language: string,
    enumName: string,
    appInfo: { appId: string; appVersionId: string; workspaceId: string }
  ): Observable<NomenclatorValue[]> {
    const { appId, appVersionId, workspaceId } = appInfo
    const _options = {
      enumName,
      language,
      application: APPLICATIONS.FLOWX,
      appId,
      appVersionId,
      workspaceId,
    }
    const enumCacheKey = this.buildEnumCacheKey(_options)

    if (!this.enumerationsCache[enumCacheKey]) {
      this.enumerationsCache[enumCacheKey] = this.#http
        .get(
          `${baseUrl}${this.getResourceUrl(appId, appVersionId, workspaceId)}/rt/enumeration/rn/${enumName}`,
          {
            ...(language && { headers: new HttpHeaders({ 'Accept-Language': language }) }),
          }
        )
        .pipe(
          map((enums: FlxEnumeration[]) =>
            _sortBy('order', enums).map(
              (enumeration) =>
                ({
                  label: enumeration.content,
                  id: enumeration.code,
                  value: enumeration.code,
                  nomenclatorId: enumeration.childContentDescription?.name,
                }) as NomenclatorValue
            )
          ),
          shareReplay({ bufferSize: 1, refCount: true })
        )
    }

    return this.enumerationsCache[enumCacheKey]
  }

  getSubstitutionTags(
    baseUrl: string,
    language: string,
    appInfo: { appId: string; appVersionId: string; workspaceId: string }
  ): Observable<any> {
    const { appId, appVersionId, workspaceId } = appInfo

    return this.#http
      .get(
        `${baseUrl}${this.getResourceUrl(appId, appVersionId, workspaceId)}/rt/substitutiontag`,
        {
          ...(language && { headers: new HttpHeaders({ 'Accept-Language': language }) }),
        }
      )
      .pipe(
        tap((response) => {
          this.#substitutionTagsStore.setSubstitutionTags(response)
        })
      )
  }

  getStaticAssets(
    baseUrl: string,
    staticAssetsPath: string,
    appInfo: { appId: string; appVersionId: string; workspaceId: string },
    options?: { invalidateCache: boolean }
  ): Observable<any> {
    const { appId, appVersionId, workspaceId } = appInfo
    const _options = {
      application: APPLICATIONS.FLOWX,
    }
    if (options?.invalidateCache) {
      this.staticAssetsCache = {}
    }
    if (!this.staticAssetsCache[_options.application]) {
      this.staticAssetsCache[_options.application] = this.#http
        .get<StaticAsset[]>(
          `${baseUrl}${this.getResourceUrl(appId, appVersionId, workspaceId)}/rt/mediafile`
        )
        .pipe(
          map((staticAssets) =>
            staticAssets.map((asset) => ({
              ...asset,
              storagePath: `${staticAssetsPath}/${asset.storagePath}`,
            }))
          ),
          tap((assets: StaticAsset[]) => {
            this.#staticAssetsStore.setStaticAssets(assets)
          }),
          shareReplay({ bufferSize: 1, refCount: true })
        )
    }
    return this.staticAssetsCache[_options.application]
  }

  getGlobalStaticAssets(baseUrl: string, staticAssetsPath: string): Observable<any> {
    return this.#http.get<StaticAsset[]>(`${baseUrl}/cms/api/media-library/images/paths`).pipe(
      map((staticAssets) =>
        staticAssets.map((asset) => ({
          ...asset,
          storagePath: `${staticAssetsPath}/${asset.storagePath}`,
        }))
      ),
      tap((assets: StaticAsset[]) => {
        this.#staticAssetsStore.setStaticAssets(assets)
      })
    )
  }

  private getResourceUrl(appId: string, appVersionId: string, workspaceId: string): string {
    return `/rtm/api/runtime-internal/wks/${workspaceId}/app/${appId}/app-version/${appVersionId}`
  }

  private buildEnumCacheKey(requestOptions: Record<string, string>): string {
    return Object.keys(requestOptions).reduce(
      (acc, option) => `${acc}-${requestOptions[option]}`,
      ''
    )
  }
}
